import React from 'react'
import iconMap from '../../assets/icons/icon-map'
import useResponsiveProp from '../../hooks/useResponsiveProp'
import { useScreenSize } from '../../hooks/useScreenSize'

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  name: keyof typeof iconMap
  size?: ResponsiveProp<'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'>
  tone?:
    | 'subtle'
    | 'light'
    | 'neutral'
    | 'action'
    | 'action--light'
    | 'positive'
    | 'negative'
    | 'caution'
    | 'ghost'
  noTone?: boolean
  rotate?: number
  tooltip?: string
}

export const Icon: React.FC<IconProps> = ({
  name,
  tone,
  size = 'l',
  noTone,
  rotate,
  tooltip,
  ...restProps
}) => {
  const sizes = {
    xs: '.8rem',
    s: '1.2rem',
    m: '1.6rem',
    l: '2.4rem',
    xl: '3.2rem',
    xxl: '4.2rem'
  }

  const defaultTones: { [key: string]: IconProps['tone'] } = {
    info: 'action',
    'info-hollow': 'action',
    success: 'positive',
    'success-hollow': 'positive',
    caution: 'caution',
    'caution-hollow': 'caution',
    critical: 'negative',
    'critical-hollow': 'negative'
  }

  if (!tone) {
    if (Object.keys(defaultTones).includes(name)) {
      tone = defaultTones[name]
    } else {
      tone = 'neutral'
    }
  }

  const device = useScreenSize()

  size = useResponsiveProp(size, device)

  if (!Object.keys(iconMap).includes(name)) return null

  const Icon = iconMap[name]

  const style: React.CSSProperties = {
    display: 'block',
    width: sizes[size as keyof typeof sizes],
    height: sizes[size as keyof typeof sizes]
  }
  if (rotate) {
    style.transform = `rotate(${rotate}deg)`
  }

  return (
    <Icon
      color={noTone ? undefined : `var(--colors-${tone})`}
      style={style}
      title={tooltip}
      {...restProps}
    />
  )
}
